import { Injectable } from "@angular/core";
import { HttpClient, HttpEvent, HttpHeaders } from "@angular/common/http";
import { API_URL } from "../config/general.config";
import { catchError, combineLatest, defer, map, Observable, of, switchMap, take, throwError } from "rxjs";
import { Attachment, AttachmentType } from "../model/file.model";
import { OfflineService } from "./offline.service";
import { ConnectionStatusService } from "./connection-status.service";
import { GeolocationService } from "./geolocation.service";
import { BaseContent } from '../model/general.model';
import { ToastrService } from "ngx-toastr";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: 'root'
})
export class AttachmentService {

  private _attachmentApi = API_URL + '/attachments';

  constructor(
    private _http: HttpClient,
    private _geolocationService: GeolocationService,
    private _offlineService: OfflineService,
    private _connectionStatusService: ConnectionStatusService,
    private _toastr: ToastrService,
    private _translate: TranslateService
  ) { }

  download(id: number): Observable<Blob> {
    if(id < 0) {
      return this._offlineService.getAttachmentFile(-id);
    }
    return this._connectionStatusService.networkStatus$
    .pipe(
      take(1),
      switchMap(status => {
        if(!status) {
          return this._offlineService.getAttachmentFileByAttachmentId(id)
          .pipe(
            catchError(error => {
              this._toastr.error(this._translate.instant(error.message));
              return throwError(() => error);
            })
          );
        }

        return this._http.get(`${this._attachmentApi}/${id}`, {
          headers: new HttpHeaders({
            'Content-Type': 'application/octet-stream',
            'Accept': 'application/octet-stream'
          }),
          observe: 'body',
          responseType: 'blob',
        })
        .pipe(
          // Konieczne, aby uniknąć podwójnego komunikatu o ew. błędzie
          catchError(error => {
            throw(new Error())
          })
        );
      })
    )

  }

  delete(id: number): Observable<void> {
    if(id < 0){
      return this._offlineService.deleteAttachment(-id).pipe(
        map(() => void 0)
      );
    }

    return of();
    //return this._http.delete<void>(`${this._attachmentApi}/${id}`);
  }

  uploadOnline(data: FormData, type: AttachmentType, taskId: number): Observable<HttpEvent<Attachment[]>> {
    const headers = new HttpHeaders({ 'ngsw-bypass': ''});

    return this._connectionStatusService.networkStatus$
    .pipe(
      take(1),
      switchMap(isOnline => {
        if(isOnline) {
          return this._http.post<Attachment[]>(`${this._attachmentApi}`,
            data,
            {
              reportProgress: true,
              observe: 'events',
              headers: headers
            }
          )
        }
        return throwError(() => new Error('OFFLINE'));
      })
    )
  }

  upload(
    data: FormData,
    type: AttachmentType,
    taskId: number,
    gps?: {longitude: number, latitude: number},
    reportStage: (translationKey: string) => void = () => undefined
  ): Observable<HttpEvent<Attachment[]> | Attachment[]> {
    return defer(() => {
      reportStage('PHOTO_UPLOAD.GETTING_GPS');

      return combineLatest({
        deviceCoords: this._geolocationService.getCurrentCoordinates(false),
        coords: of(gps),
        isOnline: this._connectionStatusService.networkStatus$,
        hasOfflineData: this._offlineService.hasOfflineData(taskId)
      });
    })
    .pipe(
      take(1),
      switchMap(({deviceCoords, coords, isOnline, hasOfflineData}) => {
        data.append("type", type);
        data.append("longitude", String(coords?.longitude));
        data.append("latitude", String(coords?.latitude));
        data.append("deviceLongitude", String(deviceCoords.longitude));
        data.append("deviceLatitude", String(deviceCoords.latitude));

        if(!isOnline || hasOfflineData) {
          return defer(() => {
            reportStage('PHOTO_UPLOAD.SAVING_OFFLINE');
            return this._offlineService.saveAttachments(data, type, taskId);
          });
        }
        else {
          return defer(() => {
            reportStage('PHOTO_UPLOAD.UPLOADING');
            return this.uploadOnline(data, type, taskId);
          });
        }
      })
    );
  }

  getOriginalBase(id: number): Observable<BaseContent> {
    return this._http.get<BaseContent>(`${this._attachmentApi}/${id}/image`)
  }
}
